import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../services/api';

// --- Types ---

interface Agent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  last_login: string | null;
}

interface AgentListResponse {
  data: Agent[];
  meta: { total: number; page: number; page_size: number };
}

interface AgentResponse {
  data: Agent;
}

// --- Schema ---

const inviteAgentSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
});

type InviteAgentForm = z.infer<typeof inviteAgentSchema>;

// --- Component ---

export default function AgentsPage(): React.ReactElement {
  const { t } = useTranslation('companies');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; agent: Agent | null }>({
    open: false,
    agent: null,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const inviteModalRef = useRef<HTMLDialogElement>(null);
  const confirmModalRef = useRef<HTMLDialogElement>(null);

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // --- Sync modals ---
  useEffect(() => {
    if (inviteOpen) inviteModalRef.current?.showModal();
    else inviteModalRef.current?.close();
  }, [inviteOpen]);

  useEffect(() => {
    if (confirmDialog.open) confirmModalRef.current?.showModal();
    else confirmModalRef.current?.close();
  }, [confirmDialog.open]);

  // --- Queries ---

  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['company-agents', paginationModel.page, paginationModel.pageSize, search],
    queryFn: () =>
      api.get<AgentListResponse>('/v1/companies/me/agents', {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        ...(search ? { search } : {}),
      }),
  });

  const agents = agentsData?.data ?? [];
  const totalRows = agentsData?.meta?.total ?? 0;
  const totalPages = Math.ceil(totalRows / paginationModel.pageSize);

  // --- Mutations ---

  const inviteMutation = useMutation({
    mutationFn: (data: InviteAgentForm) => api.post<AgentResponse>('/v1/companies/me/agents', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-agents'] });
      setInviteOpen(false);
      inviteForm.reset();
      setSnackbar({ open: true, message: t('agents.messages.inviteSuccess'), severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('agents.messages.error'), severity: 'error' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      api.patch<AgentResponse>(`/v1/companies/me/agents/${id}`, { is_active: !is_active }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['company-agents'] });
      const msgKey = variables.is_active ? 'agents.messages.deactivateSuccess' : 'agents.messages.activateSuccess';
      setSnackbar({ open: true, message: t(msgKey), severity: 'success' });
      setConfirmDialog({ open: false, agent: null });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('agents.messages.error'), severity: 'error' });
      setConfirmDialog({ open: false, agent: null });
    },
  });

  // --- Form ---

  const inviteForm = useForm<InviteAgentForm>({
    resolver: zodResolver(inviteAgentSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
    },
  });

  const handleToggleActive = (agent: Agent): void => {
    setConfirmDialog({ open: true, agent });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('agents.title')}</h1>
          <p className="text-sm text-base-content/60">{t('agents.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setInviteOpen(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
          </svg>
          {t('agents.inviteAgent')}
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        className="input input-bordered w-full mb-4"
        placeholder={t('agents.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Table */}
      <div className="overflow-x-auto bg-base-100 rounded-lg">
        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>{t('agents.columns.firstName')}</th>
                <th>{t('agents.columns.lastName')}</th>
                <th>{t('agents.columns.email')}</th>
                <th>{t('agents.columns.status')}</th>
                <th>{t('agents.columns.lastLogin')}</th>
                <th>{t('agents.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-base-content/60 py-8">
                    {tCommon('common.noResults')}
                  </td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent.id}>
                    <td className="font-medium">{agent.first_name}</td>
                    <td>{agent.last_name}</td>
                    <td>{agent.email}</td>
                    <td>
                      <span className={`badge badge-sm ${agent.is_active ? 'badge-success' : 'badge-ghost'}`}>
                        {agent.is_active ? tCommon('common.active') : tCommon('common.inactive')}
                      </span>
                    </td>
                    <td>
                      {agent.last_login ? new Date(agent.last_login).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-xs"
                        title={agent.is_active ? t('deactivate') : t('reactivate')}
                        onClick={() => handleToggleActive(agent)}
                      >
                        {agent.is_active ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-error" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-success" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalRows > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-base-200">
            <span className="text-sm text-base-content/60">
              {t('agents.page', {
                page: paginationModel.page + 1,
                total: totalPages,
              })}
            </span>
            <div className="join">
              <button
                className="join-item btn btn-sm"
                disabled={paginationModel.page === 0}
                onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                «
              </button>
              <button className="join-item btn btn-sm">
                {paginationModel.page + 1}
              </button>
              <button
                className="join-item btn btn-sm"
                disabled={paginationModel.page >= totalPages - 1}
                onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invite Agent Modal */}
      <dialog ref={inviteModalRef} className="modal" onClose={() => setInviteOpen(false)}>
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">{t('agents.inviteAgent')}</h3>
          <form
            onSubmit={inviteForm.handleSubmit((data) => inviteMutation.mutate(data))}
            noValidate
          >
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('agents.form.email')}</span></label>
              <input
                type="email"
                className={`input input-bordered w-full ${inviteForm.formState.errors.email ? 'input-error' : ''}`}
                {...inviteForm.register('email')}
              />
              {inviteForm.formState.errors.email && (
                <label className="label"><span className="label-text-alt text-error">{inviteForm.formState.errors.email.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('agents.form.firstName')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${inviteForm.formState.errors.first_name ? 'input-error' : ''}`}
                {...inviteForm.register('first_name')}
              />
              {inviteForm.formState.errors.first_name && (
                <label className="label"><span className="label-text-alt text-error">{inviteForm.formState.errors.first_name.message}</span></label>
              )}
            </div>
            <div className="form-control w-full mb-3">
              <label className="label"><span className="label-text">{t('agents.form.lastName')}</span></label>
              <input
                type="text"
                className={`input input-bordered w-full ${inviteForm.formState.errors.last_name ? 'input-error' : ''}`}
                {...inviteForm.register('last_name')}
              />
              {inviteForm.formState.errors.last_name && (
                <label className="label"><span className="label-text-alt text-error">{inviteForm.formState.errors.last_name.message}</span></label>
              )}
            </div>
            <div className="modal-action">
              <button type="button" className="btn" onClick={() => setInviteOpen(false)}>
                {tCommon('buttons.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : t('agents.inviteAgent')}
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop"><button onClick={() => setInviteOpen(false)}>close</button></form>
      </dialog>

      {/* Confirm Activate/Deactivate Modal */}
      <dialog ref={confirmModalRef} className="modal" onClose={() => setConfirmDialog({ open: false, agent: null })}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">
            {confirmDialog.agent?.is_active ? t('deactivate') : t('reactivate')}
          </h3>
          <p className="py-4 text-base-content/70">
            {confirmDialog.agent?.is_active
              ? t('agents.confirmDeactivate', {
                  name: `${confirmDialog.agent?.first_name} ${confirmDialog.agent?.last_name}`,
                })
              : t('agents.confirmActivate', {
                  name: `${confirmDialog.agent?.first_name} ${confirmDialog.agent?.last_name}`,
                })}
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setConfirmDialog({ open: false, agent: null })}>
              {tCommon('buttons.cancel')}
            </button>
            <button
              className={`btn ${confirmDialog.agent?.is_active ? 'btn-error' : 'btn-success'}`}
              disabled={toggleActiveMutation.isPending}
              onClick={() => {
                if (confirmDialog.agent) {
                  toggleActiveMutation.mutate({
                    id: confirmDialog.agent.id,
                    is_active: confirmDialog.agent.is_active,
                  });
                }
              }}
            >
              {toggleActiveMutation.isPending
                ? <span className="loading loading-spinner loading-sm" />
                : confirmDialog.agent?.is_active
                  ? t('deactivate')
                  : t('reactivate')}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setConfirmDialog({ open: false, agent: null })}>close</button>
        </form>
      </dialog>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
